import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    BarChart3,
    TrendingUp,
    TrendingDown,
    Eye,
    Heart,
    MessageCircle,
    Share2,
    Users,
    Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';

export default function Analytics() {
    const { api } = useAuth();
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('7d');

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`/analytics/overview?period=${period}`);
            setOverview(response.data);
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
        } finally {
            setLoading(false);
        }
    }, [api, period]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const statCards = [
        {
            title: 'Total Impressions',
            value: overview?.total_impressions || 0,
            icon: Eye,
            color: 'text-cyan-500',
            bg: 'bg-cyan-500/10'
        },
        {
            title: 'Total Reach',
            value: overview?.total_reach || 0,
            icon: Users,
            color: 'text-fuchsia-500',
            bg: 'bg-fuchsia-500/10'
        },
        {
            title: 'Total Likes',
            value: overview?.total_likes || 0,
            icon: Heart,
            color: 'text-red-500',
            bg: 'bg-red-500/10'
        },
        {
            title: 'Total Comments',
            value: overview?.total_comments || 0,
            icon: MessageCircle,
            color: 'text-green-500',
            bg: 'bg-green-500/10'
        },
    ];

    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    const getPlatformEmoji = (platform) => {
        const emojis = {
            instagram: '📸',
            facebook: '👤',
            twitter: '🐦',
            linkedin: '💼',
            tiktok: '🎵',
            pinterest: '📌',
            youtube: '🎬'
        };
        return emojis[platform] || '📱';
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">Analytics</h1>
                    <p className="text-muted-foreground mt-1">
                        Track your social media performance
                    </p>
                </div>
                <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-[180px]" data-testid="period-select">
                        <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7d">Last 7 days</SelectItem>
                        <SelectItem value="30d">Last 30 days</SelectItem>
                        <SelectItem value="90d">Last 90 days</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {statCards.map((stat, index) => (
                            <Card
                                key={stat.title}
                                className="hover-lift"
                                style={{ animationDelay: `${index * 0.1}s` }}
                                data-testid={`analytics-stat-${stat.title.toLowerCase().replace(' ', '-')}`}
                            >
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">
                                                {stat.title}
                                            </p>
                                            <p className="text-3xl font-bold mt-1">
                                                {formatNumber(stat.value)}
                                            </p>
                                        </div>
                                        <div className={`p-3 rounded-lg ${stat.bg}`}>
                                            <stat.icon className={`h-6 w-6 ${stat.color}`} />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <BarChart3 className="h-5 w-5 text-primary" />
                                    Performance Summary
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Posts Published</p>
                                            <p className="text-2xl font-bold">{overview?.total_posts || 0}</p>
                                        </div>
                                        <TrendingUp className="h-8 w-8 text-green-500" />
                                    </div>
                                    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Connected Accounts</p>
                                            <p className="text-2xl font-bold">{overview?.connected_accounts || 0}</p>
                                        </div>
                                        <Users className="h-8 w-8 text-cyan-500" />
                                    </div>
                                    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Avg. Engagement</p>
                                            <p className="text-2xl font-bold">
                                                {overview?.total_posts > 0
                                                    ? formatNumber(Math.round((overview?.total_likes + overview?.total_comments) / overview?.total_posts))
                                                    : '0'}
                                            </p>
                                        </div>
                                        <Heart className="h-8 w-8 text-fuchsia-500" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Share2 className="h-5 w-5 text-primary" />
                                    Platform Breakdown
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {overview?.platforms?.length > 0 ? (
                                    <div className="space-y-3">
                                        {overview.platforms.map(platform => (
                                            <div
                                                key={platform}
                                                className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className="text-2xl">{getPlatformEmoji(platform)}</span>
                                                    <span className="font-medium capitalize">{platform}</span>
                                                </div>
                                                <Badge variant="secondary">Active</Badge>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                                        <Share2 className="h-12 w-12 mb-3 opacity-50" />
                                        <p>No platforms connected</p>
                                        <Button variant="link" className="mt-2">
                                            Connect accounts
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    <Card>
                        <CardHeader>
                            <CardTitle>Analytics Note</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="p-4 rounded-lg bg-muted/50 border border-border">
                                <p className="text-muted-foreground">
                                    Analytics data is fetched from connected social media platforms. 
                                    Connect your accounts and publish posts to see detailed engagement metrics.
                                </p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Data updates may be delayed based on each platform's API rate limits.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
